import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Panel } from "../components/Surface";
import { Tags } from "../components/HeroDetail";
import { Icon } from "../app/Icon";
import { Avatar } from "../components/Avatar";
import { ReviewsSection } from "../components/ReviewsSection";
import { labelize } from "../lib/format";
import { viewProfile, requestConnection, type PublicProfile } from "../lib/connections";
import { reportContent, REPORT_REASONS } from "../lib/moderation";
import { profileTimeline, verbIcon, type Activity } from "../lib/activity";

const tileTone: Record<string, string> = {
  driver: "bg-verified-bg text-verified",
  company: "bg-info/15 text-info",
  recruiter: "bg-accent/15 text-accent-strong",
  broker_dispatcher: "bg-surface-2 text-muted",
};

const arr = (v: unknown) => (Array.isArray(v) ? v.map(String).filter(Boolean) : []);
const str = (v: unknown) => (v == null ? "" : String(v));

export function ProfileView() {
  const { id } = useParams();
  const [data, setData] = useState<PublicProfile | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reporting, setReporting] = useState(false);

  async function report(reason: (typeof REPORT_REASONS)[number]) {
    if (!id) return;
    setReporting(false);
    try {
      await reportContent({ target_type: "profile", target_id: id, reason, context: `Profile: ${data?.profile.display_name ?? id}` });
      setNote("Report submitted — our team will review it. Thanks.");
    } catch {
      setNote("Could not submit the report.");
    }
  }

  const [activity, setActivity] = useState<Activity[]>([]);
  useEffect(() => {
    if (id) viewProfile(id).then(setData).catch(() => setNote("Profile not found."));
    if (id) profileTimeline(id).then((r) => setActivity(r.activities)).catch(() => undefined);
  }, [id]);

  async function connect() {
    if (!id) return;
    setBusy(true);
    setNote(null);
    try {
      await requestConnection(id);
      setNote("Connection request sent.");
    } catch {
      setNote("Could not send the request.");
    } finally {
      setBusy(false);
    }
  }

  if (!data) return <p className="p-10 font-mono text-sm text-muted">{note ?? "Loading…"}</p>;

  const { profile, detail, relation } = data;
  const d = (detail ?? {}) as Record<string, unknown>;

  // Type-specific presentation.
  const meta: string[] = [];
  const tagGroups: { title: string; items: string[] }[] = [];
  const facts: { label: string; value: string }[] = [];
  if (profile.type === "company") {
    if (d.dot_number) meta.push(`DOT ${d.dot_number}`);
    if (arr(d.service_areas).length) meta.push(arr(d.service_areas).join(", "));
    if (arr(d.service_areas).length) tagGroups.push({ title: "Service areas", items: arr(d.service_areas) });
    if (d.legal_name) facts.push({ label: "Legal name", value: str(d.legal_name) });
    if (d.mc_number) facts.push({ label: "MC number", value: str(d.mc_number) });
    if (d.fleet_size) facts.push({ label: "Fleet size", value: `${d.fleet_size} trucks` });
  } else if (profile.type === "driver") {
    if (d.cdl_class) meta.push(`CDL ${d.cdl_class}`);
    if (d.years_experience) meta.push(`${d.years_experience} yrs`);
    if (arr(d.equipment).length) tagGroups.push({ title: "Equipment", items: arr(d.equipment) });
    if (arr(d.endorsements).length) tagGroups.push({ title: "Endorsements", items: arr(d.endorsements) });
    if (d.years_experience) facts.push({ label: "Experience", value: `${d.years_experience} years` });
    if (d.owner_operator) facts.push({ label: "Owner-operator", value: "Yes" });
  } else if (profile.type === "recruiter") {
    if (d.agency) meta.push(str(d.agency));
    if (arr(d.specialties).length) tagGroups.push({ title: "Specialties", items: arr(d.specialties) });
  } else if (profile.type === "broker_dispatcher") {
    if (d.authority_mc) meta.push(`MC ${d.authority_mc}`);
    if (arr(d.freight_types).length) tagGroups.push({ title: "Freight types", items: arr(d.freight_types) });
  }
  facts.push({ label: "Profile type", value: labelize(profile.type) });
  facts.push({ label: "Member since", value: new Date(profile.created_at).toLocaleDateString() });

  return (
    <div className="grid gap-6">
      <Link to="/connections" className="flex w-max items-center gap-1 text-sm font-bold text-muted hover:text-ink">
        <Icon name="chevron" className="h-4 w-4 rotate-90" /> Back
      </Link>

      {/* Hero */}
      <div className="flex flex-col gap-5 rounded-card border border-line bg-surface p-6 sm:flex-row sm:items-center">
        <Avatar src={profile.image_url} name={profile.display_name ?? labelize(profile.type)} className="h-20 w-20 rounded-xl text-3xl" tone={tileTone[profile.type] ?? tileTone.recruiter} />
        <div className="grid min-w-0 flex-1 gap-2">
          <h1 className="font-display text-3xl font-black tracking-tight">{profile.display_name ?? "RecruitTrucker member"}</h1>
          {profile.trust_level && profile.trust_level !== "unverified" ? (
            <span
              className={`flex w-max items-center gap-2 rounded-lg border px-3 py-1 font-mono text-sm font-bold ${
                profile.trust_level === "authority_verified"
                  ? "border-accent/50 bg-accent/10 text-accent-strong"
                  : "border-verified/40 bg-verified-bg/40 text-verified"
              }`}
            >
              <Icon name="verify" className="h-4 w-4" /> {profile.trust_label?.toUpperCase() ?? "VERIFIED"}{d.dot_number ? ` · DOT ${d.dot_number}` : ""}
            </span>
          ) : (
            profile.headline && <p className="text-sm text-muted">{profile.headline}</p>
          )}
          {meta.length > 0 && <p className="font-mono text-sm text-muted">{meta.join(" · ")}</p>}
        </div>
        {relation !== "self" && (
          <div className="flex shrink-0 items-center gap-2">
            {relation === "public" && <Button onClick={connect} disabled={busy}>{busy ? "Sending…" : "Connect"}</Button>}
            {relation === "connection" && <span className="font-mono text-sm text-verified">Connected</span>}
            <Link to={`/messages/to/${profile.id}`}>
              <Button variant="ghost"><span className="flex items-center gap-2"><Icon name="messages" className="h-4 w-4" /> Message</span></Button>
            </Link>
          </div>
        )}
      </div>

      {/* Detail panels */}
      {(tagGroups.length > 0 || (profile.headline && profile.verified)) && (
        <div className="grid gap-6 md:grid-cols-2">
          {profile.headline && profile.verified && (
            <Panel title={`About ${profile.display_name ?? ""}`.trim()}>
              <p className="text-sm text-muted">{profile.headline}</p>
            </Panel>
          )}
          {tagGroups.map((g) => (
            <Panel key={g.title} title={g.title}><Tags items={g.items} /></Panel>
          ))}
        </div>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-2 gap-4 rounded-card border border-line bg-surface p-5 sm:grid-cols-4">
        {facts.slice(0, 4).map((f) => (
          <div key={f.label} className="grid gap-1">
            <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted">{f.label}</span>
            <span className="truncate font-mono text-sm font-bold text-ink">{f.value}</span>
          </div>
        ))}
      </div>

      {/* Get in touch — contact details stay private; connect via messaging */}
      {relation !== "self" && (
        <Panel title="Get in touch">
          <p className="text-sm text-muted">
            Contact details are private. Message {profile.display_name ?? "this member"} through RecruitTrucker to connect — they can share their email or phone in a message if they choose.
          </p>
          <Link to={`/messages/to/${profile.id}`}>
            <Button><span className="flex items-center gap-2"><Icon name="messages" className="h-4 w-4" /> Send a message</span></Button>
          </Link>
        </Panel>
      )}

      {activity.length > 0 && (
        <Panel title="Recent activity">
          <ul className="grid gap-3">
            {activity.map((a) => (
              <li key={a.id} className="flex items-center gap-3">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface-2 text-muted"><Icon name={verbIcon(a.verb)} className="h-4 w-4" /></span>
                <span className="min-w-0 text-sm text-muted">{a.summary ?? a.verb.replace(/_/g, " ")}
                  <span className="block font-mono text-[10px]">{new Date(a.created_at).toLocaleDateString()}</span>
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <ReviewsSection profileId={profile.id} name={profile.display_name ?? "this member"} isSelf={relation === "self"} />

      {relation === "self" && (
        <Link to="/settings/privacy" className="text-sm font-bold text-accent-strong">This is you — edit your profile visibility →</Link>
      )}
      <p className="flex items-center gap-2 font-mono text-xs text-muted">
        <Icon name="security" className="h-3.5 w-3.5" /> Contact details are kept private — all communication happens through the messaging system.
      </p>
      {relation !== "self" && (
        reporting ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-xs text-muted">Report for:</span>
            {REPORT_REASONS.map((r) => (
              <button key={r} type="button" onClick={() => report(r)} className="rounded-md border border-line px-2 py-1 font-mono text-[11px] font-bold uppercase text-muted hover:text-danger">{r}</button>
            ))}
            <button type="button" onClick={() => setReporting(false)} className="font-mono text-[11px] text-muted">cancel</button>
          </div>
        ) : (
          <button type="button" onClick={() => setReporting(true)} className="w-max font-mono text-xs font-bold text-muted hover:text-danger">Report this profile</button>
        )
      )}
      {note && <p className="font-mono text-sm text-muted">{note}</p>}
    </div>
  );
}
